import type {
  OmsMapLocation,
  OmsResourceStatusCounts,
  OmsResourcesOverview,
} from '../types';
import type { OmsProjectPickerItem } from '../../../../services/dashboardService';
import {
  completedOf,
  computeHealthScore,
  openOf,
  scheduledOf,
  scoreDelta,
  type HealthScore,
} from './healthScore';

/* ============================================================
 * Performance model — joins the windowed resources payload with the
 * project picker (projectId → locationId) and the map slice
 * (locationId → state) into the three Explorer levels:
 * states → projects → engineers. Scores roll up by recomputing on
 * aggregated task counts, so a level's score is naturally weighted
 * by tracked-task volume — never an average of child scores.
 * ============================================================ */

export const UNMAPPED_STATE = 'Unmapped';

/** One status vocabulary, everywhere (spec §4.2). */
export type EngineerState = 'available' | 'assigned' | 'overloaded' | 'inactive';

export const ENGINEER_STATE_META: Record<EngineerState, { label: string }> = {
  available: { label: 'Available' },
  assigned: { label: 'Assigned' },
  overloaded: { label: 'Overloaded' },
  inactive: { label: 'Inactive' },
};

interface PerfBase {
  id: string;
  name: string;
  counts: OmsResourceStatusCounts;
  completed: number;
  open: number;
  scheduled: number;
  overdue: number;
  /** Null when the entity has no scheduled work in the window. */
  score: HealthScore | null;
  /** Points vs the previous equivalent window; null when either side lacks data. */
  delta: number | null;
}

export interface EngineerPerf extends PerfBase {
  kind: 'engineer';
  roleName: string;
  state: EngineerState;
  daysSinceLastSubmit: number | null;
  lastSubmittedAt: string | null;
  projectCount: number;
  projectNames: string[];
}

export interface ProjectPerf extends PerfBase {
  kind: 'project';
  locationId: string | null;
  locationName: string;
  stateName: string;
  engineerCount: number;
}

/** One site/location — the level managers track progress by. */
export interface LocationPerf extends PerfBase {
  kind: 'location';
  stateName: string;
  projectCount: number;
  engineerCount: number;
}

export interface StatePerf extends PerfBase {
  kind: 'state';
  locationCount: number;
  projectCount: number;
  engineerCount: number;
}

export type PerfNode = EngineerPerf | ProjectPerf | LocationPerf | StatePerf;

export interface PerformanceModel {
  states: StatePerf[];
  /** All locations with activity in the window, across every state. */
  locations: LocationPerf[];
  /** All projects with activity in the window, across every state. */
  projects: ProjectPerf[];
  /** projectId → state name, for scoping engineers. */
  stateOfProject: Map<string, string>;
  /** state name → its location ids (feeds per-state trend queries). */
  locationsOfState: Map<string, string[]>;
}

/** Synthetic location id for projects with no mapped location. */
export const UNMAPPED_LOCATION_ID = 'unmapped';

const emptyCounts = (): OmsResourceStatusCounts => ({
  pass: 0,
  fail: 0,
  inprogress: 0,
  due: 0,
  overdue: 0,
  hold: 0,
});

const addCounts = (into: OmsResourceStatusCounts, from: OmsResourceStatusCounts) => {
  into.pass += from.pass;
  into.fail += from.fail;
  into.inprogress += from.inprogress;
  into.due += from.due;
  into.overdue += from.overdue;
  into.hold += from.hold;
};

function baseOf(id: string, name: string, counts: OmsResourceStatusCounts, days: number | null) {
  return {
    id,
    name,
    counts,
    completed: completedOf(counts),
    open: openOf(counts),
    scheduled: scheduledOf(counts),
    overdue: counts.overdue,
    score: computeHealthScore({ counts, daysSinceLastSubmit: days }),
    delta: null as number | null,
  };
}

interface ProjectAccum {
  projectId: string;
  projectName: string;
  counts: OmsResourceStatusCounts;
  engineerIds: Set<string>;
  minDays: number | null;
}

/** Aggregate one windowed overview into per-project accumulators. */
function accumulateProjects(overview: OmsResourcesOverview | null): Map<string, ProjectAccum> {
  const byProject = new Map<string, ProjectAccum>();
  for (const row of overview?.resources ?? []) {
    for (const seg of row.projects) {
      let acc = byProject.get(seg.projectId);
      if (!acc) {
        acc = {
          projectId: seg.projectId,
          projectName: seg.projectName,
          counts: emptyCounts(),
          engineerIds: new Set(),
          minDays: null,
        };
        byProject.set(seg.projectId, acc);
      }
      addCounts(acc.counts, seg.statusCounts);
      acc.engineerIds.add(row.resourceId);
      if (row.daysSinceLastSubmit !== null) {
        acc.minDays =
          acc.minDays === null ? row.daysSinceLastSubmit : Math.min(acc.minDays, row.daysSinceLastSubmit);
      }
    }
  }
  return byProject;
}

/**
 * Engineer state: Inactive (no recent submission) → Overloaded (top-quartile
 * open work that outweighs completed) → Assigned (has open work) → Available.
 */
function engineerStateOf(
  row: { isWorking: boolean; open: number; completed: number },
  openP75: number
): EngineerState {
  if (!row.isWorking) return 'inactive';
  if (row.open > 0 && row.open >= openP75 && row.open > row.completed) return 'overloaded';
  if (row.open > 0) return 'assigned';
  return 'available';
}

/**
 * Build engineer rows for a scope. When `projectIds` is given, each
 * engineer's counts are restricted to their segments inside that scope —
 * so a score at project level grades only their work on those projects.
 */
export function buildEngineers(
  current: OmsResourcesOverview | null,
  previous: OmsResourcesOverview | null,
  projectIds: Set<string> | null
): EngineerPerf[] {
  const scopedCounts = (row: NonNullable<typeof current>['resources'][number]) => {
    const counts = emptyCounts();
    const names: string[] = [];
    for (const seg of row.projects) {
      if (projectIds && !projectIds.has(seg.projectId)) continue;
      addCounts(counts, seg.statusCounts);
      names.push(seg.projectName);
    }
    return { counts, names };
  };

  const prevByResource = new Map(
    (previous?.resources ?? []).map((row) => [row.resourceId, row])
  );

  const drafts = (current?.resources ?? [])
    .map((row) => {
      const { counts, names } = scopedCounts(row);
      return { row, counts, names, scheduled: scheduledOf(counts), open: openOf(counts) };
    })
    .filter((d) => d.scheduled > 0);

  const opens = drafts.map((d) => d.open).sort((a, b) => a - b);
  const openP75 = opens.length > 0 ? (opens[Math.floor(opens.length * 0.75)] ?? 0) : 0;

  const engineers = drafts.map(({ row, counts, names }) => {
    const base = baseOf(row.resourceId, row.resourceName, counts, row.daysSinceLastSubmit);
    const prevRow = prevByResource.get(row.resourceId);
    let prevScore: HealthScore | null = null;
    if (prevRow) {
      const prevScoped = scopedCounts(prevRow);
      prevScore = computeHealthScore({
        counts: prevScoped.counts,
        daysSinceLastSubmit: prevRow.daysSinceLastSubmit,
      });
    }
    return {
      ...base,
      kind: 'engineer' as const,
      roleName: row.roleName,
      state: engineerStateOf(
        { isWorking: row.isWorking, open: base.open, completed: base.completed },
        Math.max(openP75, 1)
      ),
      daysSinceLastSubmit: row.daysSinceLastSubmit,
      lastSubmittedAt: row.lastSubmittedAt,
      projectCount: names.length,
      projectNames: names,
      delta: scoreDelta(base.score, prevScore),
    };
  });

  return engineers.sort(byScoreDesc);
}

/** Rank comparator: scored entities first (high → low), unscored last. */
export function byScoreDesc(a: PerfBase, b: PerfBase): number {
  if (a.score && b.score) return b.score.score - a.score.score || b.scheduled - a.scheduled;
  if (a.score) return -1;
  if (b.score) return 1;
  return b.scheduled - a.scheduled;
}

/** Aggregate rollup shared by the location and state levels. */
interface GroupAccum {
  counts: OmsResourceStatusCounts;
  engineerIds: Set<string>;
  projectCount: number;
  locationIds: Set<string>;
  minDays: number | null;
}

const emptyGroup = (): GroupAccum => ({
  counts: emptyCounts(),
  engineerIds: new Set(),
  projectCount: 0,
  locationIds: new Set(),
  minDays: null,
});

function addProjectToGroup(group: GroupAccum, acc: ProjectAccum, locationId: string) {
  addCounts(group.counts, acc.counts);
  for (const id of acc.engineerIds) group.engineerIds.add(id);
  group.projectCount += 1;
  group.locationIds.add(locationId);
  if (acc.minDays !== null) {
    group.minDays = group.minDays === null ? acc.minDays : Math.min(group.minDays, acc.minDays);
  }
}

/**
 * Full model: projects joined to their location and state, rolled up
 * location-wise (one location can hold several projects — this is the
 * level managers track progress by) and then state-wise. Projects whose
 * location is unknown group under an honest "Unmapped" bucket, never
 * silently dropped.
 */
export function buildPerformanceModel(
  current: OmsResourcesOverview | null,
  previous: OmsResourcesOverview | null,
  picker: OmsProjectPickerItem[],
  mapLocations: OmsMapLocation[],
  /** locationId → display name (locations without map coordinates included). */
  locationNames: Map<string, string> = new Map()
): PerformanceModel {
  const locationOfProject = new Map(picker.map((p) => [p.id, p.locationId]));
  const stateOfLocation = new Map(
    mapLocations.map((loc) => [loc.locationId, (loc.state ?? '').trim() || UNMAPPED_STATE])
  );
  const mapNames = new Map(mapLocations.map((loc) => [loc.locationId, loc.name]));
  const locationsOfState = new Map<string, string[]>();
  for (const [locationId, stateName] of stateOfLocation) {
    const list = locationsOfState.get(stateName) ?? [];
    list.push(locationId);
    locationsOfState.set(stateName, list);
  }

  const locationIdOf = (projectId: string) => locationOfProject.get(projectId) ?? UNMAPPED_LOCATION_ID;
  const locationNameOf = (locationId: string) =>
    locationId === UNMAPPED_LOCATION_ID
      ? 'Unmapped location'
      : (locationNames.get(locationId) ?? mapNames.get(locationId) ?? 'Unnamed location');
  const stateNameOf = (locationId: string) =>
    locationId === UNMAPPED_LOCATION_ID ? UNMAPPED_STATE : (stateOfLocation.get(locationId) ?? UNMAPPED_STATE);

  const currentAgg = accumulateProjects(current);
  const previousAgg = accumulateProjects(previous);

  const stateOfProject = new Map<string, string>();
  const projects: ProjectPerf[] = [];
  for (const acc of currentAgg.values()) {
    const locationId = locationIdOf(acc.projectId);
    const stateName = stateNameOf(locationId);
    stateOfProject.set(acc.projectId, stateName);
    const base = baseOf(acc.projectId, acc.projectName, acc.counts, acc.minDays);
    const prev = previousAgg.get(acc.projectId);
    const prevScore = prev
      ? computeHealthScore({ counts: prev.counts, daysSinceLastSubmit: prev.minDays })
      : null;
    projects.push({
      ...base,
      kind: 'project',
      locationId: locationId === UNMAPPED_LOCATION_ID ? null : locationId,
      locationName: locationNameOf(locationId),
      stateName,
      engineerCount: acc.engineerIds.size,
      delta: scoreDelta(base.score, prevScore),
    });
  }
  projects.sort(byScoreDesc);

  const rollup = (agg: Map<string, ProjectAccum>, keyOf: (projectId: string) => string) => {
    const groups = new Map<string, GroupAccum>();
    for (const acc of agg.values()) {
      const key = keyOf(acc.projectId);
      let group = groups.get(key);
      if (!group) {
        group = emptyGroup();
        groups.set(key, group);
      }
      addProjectToGroup(group, acc, locationIdOf(acc.projectId));
    }
    return groups;
  };

  const scoreOfGroup = (group: GroupAccum | undefined) =>
    group ? computeHealthScore({ counts: group.counts, daysSinceLastSubmit: group.minDays }) : null;

  // Level 3 → 2: locations.
  const currentLocations = rollup(currentAgg, locationIdOf);
  const previousLocations = rollup(previousAgg, locationIdOf);
  const locations: LocationPerf[] = [];
  for (const [locationId, group] of currentLocations) {
    const base = baseOf(locationId, locationNameOf(locationId), group.counts, group.minDays);
    locations.push({
      ...base,
      kind: 'location',
      stateName: stateNameOf(locationId),
      projectCount: group.projectCount,
      engineerCount: group.engineerIds.size,
      delta: scoreDelta(base.score, scoreOfGroup(previousLocations.get(locationId))),
    });
  }
  locations.sort(byScoreDesc);

  // Level 2 → 1: states.
  const stateKeyOf = (projectId: string) => stateNameOf(locationIdOf(projectId));
  const currentStates = rollup(currentAgg, stateKeyOf);
  const previousStates = rollup(previousAgg, stateKeyOf);
  const states: StatePerf[] = [];
  for (const [stateName, group] of currentStates) {
    const base = baseOf(stateName, stateName, group.counts, group.minDays);
    states.push({
      ...base,
      kind: 'state',
      locationCount: group.locationIds.size,
      projectCount: group.projectCount,
      engineerCount: group.engineerIds.size,
      delta: scoreDelta(base.score, scoreOfGroup(previousStates.get(stateName))),
    });
  }
  states.sort(byScoreDesc);

  return { states, locations, projects, stateOfProject, locationsOfState };
}
